import React, { Component } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import Cookies from 'js-cookie';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { Fetcher } from '../../../Helpers/fetcher.js';
import { Notify } from '../../../Helpers/notification.js';
import './DocumentView.css';

const DOCUMENT_TYPE_LABELS = {
	SIMPLE_DOCUMENT: 'Simple Document',
	TEMPLATE_1: 'TEMPLATE 1',
	TEMPLATE_2: 'TEMPLATE 2',
	TEMPLATE_3: 'TEMPLATE 3',
	TEMPLATE_4: 'TEMPLATE 4',
	TEMPLATE_5: 'TEMPLATE 5',
	TEMPLATE_6: 'TEMPLATE 6',
	TEMPLATE_7: 'TEMPLATE 7',
	TEMPLATE_8: 'TEMPLATE 8',
	TEMPLATE_9: 'TEMPLATE 9'
};

class DocumentView extends Component {
	constructor(props) {
		super(props);
		this.objectUrl = null;
		this.state = {
			document: null,
			isLoading: true,
			previewType: null,
			previewContent: null,
			previewError: null
		};
	}

	componentDidMount() {
		this.loadDocument();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.match.params.documentId !== this.props.match.params.documentId) {
			this.loadDocument();
		}
	}

	componentWillUnmount() {
		if (this.objectUrl) {
			window.URL.revokeObjectURL(this.objectUrl);
		}
	}

	getExtension = (fileName) => {
		if (!fileName) return '';
		const idx = fileName.lastIndexOf('.');
		return idx === -1 ? '' : fileName.substring(idx + 1).toLowerCase();
	};

	loadDocument = () => {
		const id = this.props.match.params.documentId;
		this.setState({ isLoading: true, document: null, previewType: null, previewContent: null, previewError: null });

		Fetcher.get(this.props.app.apiPath + '/api/obd/document/' + id)
			.then(doc => {
				if (!doc || !doc.id) {
					this.setState({ isLoading: false });
					return;
				}
				this.setState({ document: doc });
				return this.loadPreview(doc);
			})
			.catch(err => {
				console.log('Error in fetching document', err);
			})
			.finally(() => this.setState({ isLoading: false }));
	}

	loadPreview = (doc) => {
		const token = Cookies.get('token');
		const ext = this.getExtension(doc.originalFileName);

		return fetch(this.props.app.apiPath + '/api/obd/document/download/' + doc.id, {
			headers: { 'Authorization': 'Bearer ' + token }
		})
			.then(res => {
				if (!res.ok) throw new Error('Failed to load file');
				return res.blob();
			})
			.then(blob => {
				if (ext === 'pdf') {
					this.objectUrl = window.URL.createObjectURL(blob);
					this.setState({ previewType: 'pdf', previewContent: this.objectUrl });
					return;
				}
				if (ext === 'txt') {
					return blob.text().then(text => this.setState({ previewType: 'text', previewContent: text }));
				}
				if (ext === 'csv') {
					return blob.text().then(text => this.setState({ previewType: 'csv', previewContent: text }));
				}
				if (ext === 'xls' || ext === 'xlsx') {
					return blob.arrayBuffer().then(buffer => {
						const workbook = XLSX.read(buffer, { type: 'array' });
						const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
						const html = XLSX.utils.sheet_to_html(firstSheet);
						this.setState({ previewType: 'html', previewContent: html });
					});
				}
				if (ext === 'doc' || ext === 'docx') {
					return blob.arrayBuffer().then(buffer =>
						mammoth.convertToHtml({ arrayBuffer: buffer })
							.then(result => this.setState({ previewType: 'html', previewContent: result.value }))
							.catch(() => this.setState({ previewError: 'Preview is not available for this file. Please download it to view.' }))
					);
				}
				this.setState({ previewError: 'Preview is not available for this file type. Please download it to view.' });
			})
			.catch(err => {
				console.log('Error loading document preview', err);
				this.setState({ previewError: 'Preview is not available for this file. Please download it to view.' });
			});
	}

	downloadDocument = () => {
		const { document: doc } = this.state;
		if (!doc) return;
		const token = Cookies.get('token');
		fetch(this.props.app.apiPath + '/api/obd/document/download/' + doc.id, {
			headers: { 'Authorization': 'Bearer ' + token }
		})
			.then(res => {
				if (!res.ok) throw new Error('Download failed');
				return res.blob();
			})
			.then(blob => {
				const link = window.document.createElement('a');
				link.href = window.URL.createObjectURL(blob);
				link.download = doc.originalFileName;
				link.click();
			})
			.catch(err => {
				console.log('Error in downloading Document', err);
				Notify.notify('Error in downloading Document');
			});
	}

	renderCsvTable = (text) => {
		const rows = text.split(/\r?\n/).filter(row => row.length > 0);
		return (
			<table className="table table-bordered table-sm">
				<tbody>
					{rows.map((row, i) => (
						<tr key={i}>
							{row.split(',').map((cell, j) => <td key={j}>{cell}</td>)}
						</tr>
					))}
				</tbody>
			</table>
		);
	}

	renderPreview = () => {
		const { previewType, previewContent, previewError } = this.state;

		if (previewError) {
			return <div className="text-muted">{previewError}</div>;
		}
		if (!previewType) {
			return <div className="text-center"><Spinner animation="border" size="sm" /> Loading preview...</div>;
		}
		if (previewType === 'pdf') {
			return <iframe title="document-preview" src={previewContent} className="document-preview-frame" />;
		}
		if (previewType === 'text') {
			return <pre className="document-preview-text">{previewContent}</pre>;
		}
		if (previewType === 'csv') {
			return <div className="document-preview-table">{this.renderCsvTable(previewContent)}</div>;
		}
		if (previewType === 'html') {
			return <div className="document-preview-html" dangerouslySetInnerHTML={{ __html: previewContent }} />;
		}
		return null;
	}

	render() {
		const { document: doc, isLoading } = this.state;

		return (
			<Card>
				<Card.Header className="d-flex justify-content-between align-items-center">
					<span>{doc ? doc.documentName : 'Document'}</span>
					{doc && <Button variant="primary" size="sm" onClick={this.downloadDocument}>Download</Button>}
				</Card.Header>
				<Card.Body>
					{isLoading ? (
						<div className="text-center"><Spinner animation="border" /></div>
					) : !doc ? (
						<div>Document not found.</div>
					) : (
						<>
							<div className="text-muted mb-3">{DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType} &middot; {doc.originalFileName}</div>
							{this.renderPreview()}
						</>
					)}
				</Card.Body>
			</Card>
		);
	}
}

export default DocumentView;
